<template>
  <b-dropdown
    variant="blue"
    size="sm"
    :disabled="isExportBusy || !url"
    class="file-export"
    right
  >
    <template #button-content>
      <span v-if="!isExportBusy">Export</span>
      <b-spinner v-else small />
    </template>
    <b-dropdown-item @click="exportAs('xlsx')">Export as Excel</b-dropdown-item>
    <b-dropdown-item @click="exportAs('csv')">Export as CSV</b-dropdown-item>
  </b-dropdown>
</template>

<script lang="ts">
import type { AxiosInstance } from 'axios'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { toErrorList } from '@/utils/apiErrors'

type ExportFormat = 'xlsx' | 'csv'

const MIME: Record<ExportFormat, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

/**
 * A failed export's message. The request asks for a blob, so a JSON error body arrives as one; a body
 * that is not JSON gets toErrorList's generic message.
 */
async function exportErrorMessage(error: any): Promise<string> {
  let data = error?.response?.data
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text())
    } catch {
      data = null
    }
  }

  return toErrorList(data).join(' ')
}

/**
 * The standard report Export dropdown, for endpoints that build the file server-side
 * (`?file_format=csv|xlsx`). Give it the export `url` (null while there is nothing to
 * export) and the download `filename` without extension; it does the rest. Pass `client` to
 * fetch through another axios instance, such as `cfAxios` for a gateway route; it defaults to
 * the Django client.
 */
@Component
export default class FileExportDropdown extends Vue {
  @Prop({ type: String, default: null }) url!: string | null
  @Prop({ type: String, required: true }) filename!: string
  @Prop({ type: [Function, Object], default: null })
  client!: AxiosInstance | null

  isExportBusy = false

  async exportAs(format: ExportFormat = 'xlsx') {
    if (!this.url) return

    this.isExportBusy = true
    try {
      // The server stamps every date in this zone, so the file reads like the screen.
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const response = await (this.client ?? this.axios).get(this.url, {
        responseType: 'blob',
        params: { file_format: format, tz }
      })
      const blob = new Blob([response.data], { type: MIME[format] })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.filename}.${format}`
      link.click()
      URL.revokeObjectURL(link.href)
    } catch (error) {
      this.$toasted.global.error({ message: await exportErrorMessage(error) })
    } finally {
      this.isExportBusy = false
    }
  }
}
</script>
